import { db, auth } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import packageJson from '../../package.json';

export interface LogContext {
    [key: string]: any;
}

export type LogLevel = 'info' | 'warn' | 'error';

// Patterns that indicate transient network issues (not worth logging remotely)
const TRANSIENT_ERROR_PATTERNS = [
    /network\s*error/i,
    /failed\s*to\s*fetch/i,
    /offline/i,
    /timeout/i,
    /connection\s*(failed|refused|reset)/i,
    /internet\s*connection/i,
    /net::ERR_/i,
    /ECONNREFUSED/i,
    /ETIMEDOUT/i,
    /request\s*was\s*cancelled/i,
    /request\s*was\s*aborted/i,
];

function isTransientNetworkError(message: string): boolean {
    return TRANSIENT_ERROR_PATTERNS.some(pattern => pattern.test(message));
}

class Logger {
    private readonly collectionName = 'client_logs';
    private readonly appVersion = packageJson.version;

    async log(level: LogLevel, message: string | Error, context: LogContext = {}) {
        const errorMessage = message instanceof Error ? message.message : message;
        const errorStack = message instanceof Error ? message.stack : undefined;
        const errorName = message instanceof Error ? message.name : undefined;

        // Always log to console in development
        if (import.meta.env.DEV) {
            if (level === 'error') {
                console.error('[Logger]', errorMessage, context, errorStack);
            } else if (level === 'warn') {
                console.warn('[Logger]', errorMessage, context);
            } else {
                console.log('[Logger]', errorMessage, context);
            }
        }

        // Skip logging transient network errors to Firestore
        // Check both the main message and context.error (used by api-client)
        const contextError = typeof context.error === 'string' ? context.error : '';
        if (isTransientNetworkError(errorMessage) || isTransientNetworkError(contextError)) {
            if (import.meta.env.DEV) {
                console.log('[Logger] Skipped remote logging (transient network error)');
            }
            return;
        }

        try {
            const user = auth.currentUser;

            const logEntry = {
                level,
                message: errorMessage,
                timestamp: serverTimestamp(),
                userId: user?.uid || 'anonymous',
                appVersion: this.appVersion,
                userAgent: navigator.userAgent,
                url: window.location.href,
                ...context,
                ...(errorStack && { stack: errorStack }),
                ...(errorName && { errorName }),
            };

            await addDoc(collection(db, this.collectionName), logEntry);

        } catch (e) {
            // Fallback to console if logging fails to prevent infinite loops
            console.error('Failed to write log to Firestore:', e);
        }
    }

    async error(message: string | Error, context: LogContext = {}) {
        return this.log('error', message, context);
    }

    async warn(message: string, context: LogContext = {}) {
        return this.log('warn', message, context);
    }

    async info(message: string, context: LogContext = {}) {
        return this.log('info', message, context);
    }
}

export const logger = new Logger();

